import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { packageService } from '../../services/packageService';
import { advancedBillingService } from '../../services/advancedBillingService';
import PackageCard from './components/PackageCard';
import PackageCreationModal from './components/PackageCreationModal';
import PackageFilters from './components/PackageFilters';
import PricingManagementModal from './components/PricingManagementModal';

export function CommercialPackagesContent() {
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    tier: 'all',
    status: 'all',
    countryId: 'all',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetchPackages();
    fetchCountries();
  }, [filters]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await packageService?.getAll(filters);
      setPackages(data);
    } catch (err) {
      console.error('Error fetching commercial packages:', err);
      setError(err?.message || 'Erreur lors du chargement des packs');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await advancedBillingService?.getAllCountries();
      setCountries(data);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setCountries([]);
    }
  };

  const handleCreatePackage = async (packageData) => {
    try {
      await packageService?.create(packageData);
      await fetchPackages();
      setShowCreationModal(false);
      setSelectedPackage(null);
    } catch (err) {
      console.error('Error creating package:', err);
      alert('Erreur lors de la création du pack: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleUpdatePackage = async (id, packageData) => {
    try {
      await packageService?.update(id, packageData);
      await fetchPackages();
      setShowCreationModal(false);
      setSelectedPackage(null);
    } catch (err) {
      console.error('Error updating package:', err);
      alert('Erreur lors de la mise à jour du pack: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce pack ?')) return;
    try {
      await packageService?.delete(id);
      await fetchPackages();
    } catch (err) {
      console.error('Error deleting package:', err);
      alert('Erreur lors de la suppression du pack: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleEditPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowCreationModal(true);
  };

  const handleUpdatePricing = async (id, pricingData) => {
    try {
      await packageService?.updatePricing(id, pricingData);
      await fetchPackages();
      setShowPricingModal(false);
      setSelectedPackage(null);
    } catch (err) {
      console.error('Error updating pricing:', err);
      alert('Erreur lors de la mise à jour des prix: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await packageService?.updateStatus(id, newStatus);
      await fetchPackages();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleSavePackage = (packageData) => {
    if (selectedPackage) {
      handleUpdatePackage(selectedPackage?.id, packageData);
    } else {
      handleCreatePackage(packageData);
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Packs commerciaux</h2>
          <p className="text-muted-foreground mt-1">Créez et gérez vos offres commerciales</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          onClick={() => {
            setSelectedPackage(null);
            setShowCreationModal(true);
          }}
        >
          Créer un pack
        </Button>
      </div>

      <PackageFilters
        filters={filters}
        onFilterChange={setFilters}
        onFiltersChange={setFilters}
        countries={countries}
      />

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
          <Icon name="AlertCircle" size={20} className="text-error" />
          <div>
            <p className="font-medium text-error">Erreur</p>
            <p className="text-sm text-error/80">{error}</p>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Chargement des packs...</p>
        </div>
      )}

      {!loading && !error && packages?.length === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">Aucun pack trouvé</h3>
          <p className="text-muted-foreground mb-6">Commencez par créer votre premier pack commercial</p>
          <Button
            variant="default"
            iconName="Plus"
            onClick={() => {
              setSelectedPackage(null);
              setShowCreationModal(true);
            }}
          >
            Créer un pack
          </Button>
        </div>
      )}

      {!loading && !error && packages?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages?.map((pkg) => (
            <PackageCard
              key={pkg?.id}
              package={pkg}
              onEdit={() => handleEditPackage(pkg)}
              onDelete={() => handleDeletePackage(pkg?.id)}
              onToggleStatus={() => handleToggleStatus(pkg?.id, pkg?.status)}
              onManagePricing={() => {
                setSelectedPackage(pkg);
                setShowPricingModal(true);
              }}
            />
          ))}
        </div>
      )}

      {showCreationModal && (
        <PackageCreationModal
          package={selectedPackage}
          countries={countries}
          onClose={() => {
            setShowCreationModal(false);
            setSelectedPackage(null);
          }}
          onSave={handleSavePackage}
        />
      )}

      {showPricingModal && selectedPackage && (
        <PricingManagementModal
          package={selectedPackage}
          onClose={() => {
            setShowPricingModal(false);
            setSelectedPackage(null);
          }}
          onSave={(pricingData) => handleUpdatePricing(selectedPackage?.id, pricingData)}
        />
      )}
    </>
  );
}
